import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Format currency in Turkish format (₺ 1.234,56)
export function formatCurrency(amount) {
  if (amount === null || amount === undefined) return "₺ 0,00";
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2,
  }).format(amount);
}

// Format amount input to Turkish format with thousand separators (100.000,50)
export function formatTurkishAmount(value) {
  if (!value && value !== 0) return '';
  let str = String(value);
  // Remove everything except digits and comma
  let cleaned = str.replace(/[^\d,]/g, '');
  if (!cleaned) return '';
  // Only allow one comma
  const parts = cleaned.split(',');
  // Format integer part with dots
  const intPart = parts[0];
  const formatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  if (parts.length >= 2) {
    return formatted + ',' + parts[1].slice(0, 2);
  }
  return formatted;
}

// Parse Turkish formatted amount to number: "100.000,50" -> 100000.50
export function parseTurkishAmount(value) {
  if (!value && value !== 0) return 0;
  const cleaned = String(value).replace(/\./g, '').replace(',', '.');
  return parseFloat(cleaned) || 0;
}

// Format date in Turkish format (DD.MM.YYYY)
export function formatDate(dateString) {
  if (!dateString) return "";
  
  // If already in DD.MM.YYYY format, return as is
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateString)) {
    return dateString;
  }
  
  const date = new Date(dateString);
  return date.toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

// Parse Turkish date (DD.MM.YYYY) to Date object
export function parseTurkishDate(dateString) {
  if (!dateString) return null;
  const [day, month, year] = dateString.split('.');
  return new Date(year, month - 1, day);
}

// Format Date object to Turkish format (DD.MM.YYYY)
export function toTurkishDate(date) {
  if (!date) return "";
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}

// Get current date in Turkish format
export function getTodayTurkish() {
  return toTurkishDate(new Date());
}

// Get current month in MM.YYYY format
export function getCurrentMonth() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const year = now.getFullYear();
  return `${month}.${year}`;
}

// Check if date is overdue
export function isOverdue(dateString) {
  const date = parseTurkishDate(dateString);
  if (!date) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
}

// Check if date is upcoming (within X days)
export function isUpcoming(dateString, days = 7) {
  const date = parseTurkishDate(dateString);
  if (!date) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const futureDate = new Date(today);
  futureDate.setDate(futureDate.getDate() + days);
  return date >= today && date <= futureDate;
}

// Get relative time text in Turkish
export function getRelativeTime(dateString) {
  const date = parseTurkishDate(dateString);
  if (!date) return "";
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  
  const diffTime = date.getTime() - today.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return "Bugün";
  if (diffDays === 1) return "Yarın";
  if (diffDays === -1) return "Dün";
  if (diffDays > 0 && diffDays <= 7) return `${diffDays} gün sonra`;
  if (diffDays < 0) return `${Math.abs(diffDays)} gün geçti`;
  
  return formatDate(dateString);
}

// Status badge variants
export function getStatusVariant(status) {
  switch (status) {
    case 'paid':
      return 'success';
    case 'pending':
      return 'warning';
    case 'overdue':
      return 'destructive';
    case 'bounced':
    case 'cancelled':
      return 'secondary';
    default:
      return 'default';
  }
}

// Turkish status labels
export const statusLabels = {
  pending: 'Bekliyor',
  paid: 'Ödendi',
  partial: 'Kısmi Ödendi',
  bounced: 'Karşılıksız',
  cancelled: 'İptal',
  overdue: 'Gecikmiş',
  upcoming: 'Yaklaşıyor',
};

// Get Turkish month name
export function getTurkishMonth(monthIndex) {
  const months = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
  ];
  return months[monthIndex];
}

// Format month period (MM.YYYY to Ocak 2025)
export function formatMonthPeriod(period) {
  if (!period) return "";
  const [month, year] = period.split('.');
  return `${getTurkishMonth(parseInt(month) - 1)} ${year}`;
}

// Truncate text
export function truncate(str, length = 30) {
  if (!str) return "";
  return str.length > length ? `${str.substring(0, length)}...` : str;
}

// Debounce function
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}
